"use client";

import { useState } from "react";
import Link from "next/link";
import SectionHeader from "@/components/ui/section-header";
import { ArrowUpRight, Check, Copy } from "@/components/icons";
import { cn } from "@/lib/utils";
import { DOCS, GITHUB_MCP_URL } from "@/lib/constants";
import { DEV_TABS, type DevLink } from "./data";
import DevCodeDisplay from "@/components/sections/products/shared/dev-code-display";

const LINKS: DevLink[] = [
  { label: "Residential proxy docs", href: DOCS.residentialProxies },
  { label: "Residential API reference", href: DOCS.apiPerGbResidential },
  { label: "MCP server", href: "/tools/mcp-server" },
  { label: "MCP server on GitHub", href: GITHUB_MCP_URL },
  { label: "Chrome extension", href: "/tools/chrome-extension" },
  { label: "Playwright proxy setup guide", href: "/blog/playwright-proxy-python-nodejs/" },
  { label: "Browser Use integration guide", href: "/blog/browser-use-residential-proxies/" },
  { label: "Proxy session management for AI agents", href: "/blog/proxy-sessions-for-ai-agents/" },
];

export default function ResidentialForDevelopers() {
  const [activeTab, setActiveTab] = useState(DEV_TABS[0].id);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = async (id: string, code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 2000);
  };

  return (
    <section
      className="py-section-sm border-border border-b"
      aria-labelledby="residential-for-developers-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 max-w-3xl">
          <SectionHeader
            eyebrow="Built for Developers"
            id="residential-for-developers-heading"
            heading="Residential Proxies for Developers"
            body="HTTP, HTTPS, and SOCKS5 carry your agent's or scraper's traffic through Proxidize residential IPs. The REST API and MCP server manage those resources — credentials, rotation, location — not the traffic. Your app still runs the browser and extracts the data."
          />
        </div>

        <div className="border-border bg-card overflow-hidden rounded-2xl border">
          <div
            role="tablist"
            aria-label="Code examples by language"
            className="border-border scrollbar-hide flex items-center gap-0.5 overflow-x-auto border-b px-2"
          >
            {DEV_TABS.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`dev-tab-${tab.id}`}
                  aria-selected={isActive}
                  aria-controls={`dev-panel-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "shrink-0 border-b-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors",
                    isActive
                      ? "border-brand-green text-foreground"
                      : "text-muted-foreground hover:text-foreground border-transparent"
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Every panel is rendered (not just the active one) so all five
              snippets are present in the server-rendered HTML for crawlers
              that don't execute JS — only the active one is visible. */}
          {DEV_TABS.map((tab) => (
            <div
              key={tab.id}
              id={`dev-panel-${tab.id}`}
              role="tabpanel"
              aria-labelledby={`dev-tab-${tab.id}`}
              className={cn(tab.id !== activeTab && "hidden")}
            >
              <div className="border-border flex items-center justify-between border-b px-5 py-3">
                <span className="text-muted-foreground bg-foreground/5 rounded px-2 py-0.5 font-mono text-xs font-medium">
                  {tab.lang}
                </span>
                <button
                  type="button"
                  onClick={() => handleCopy(tab.id, tab.code)}
                  className="text-muted-foreground hover:text-foreground -m-2 flex items-center gap-1.5 p-2 text-xs transition-colors"
                  aria-label="Copy code"
                >
                  {copiedId === tab.id ? <Check size={12} /> : <Copy size={12} />}
                  {copiedId === tab.id ? "Copied!" : "Copy"}
                </button>
              </div>
              <div className="overflow-x-auto">
                <DevCodeDisplay code={tab.code} language={tab.lang} />
              </div>
            </div>
          ))}
        </div>

        <p className="text-muted-foreground mt-6 max-w-3xl text-sm leading-relaxed">
          Country targeting is set directly in the username, as shown above. City, ISP, and session
          mode (rotating or sticky) are configured per access point in the dashboard or Session
          Builder — generate the credentials there and drop them into any of the snippets above.
        </p>

        <p className="text-muted-foreground mt-8 mb-3 text-xs font-bold tracking-wider uppercase">
          Developer resources
        </p>
        <ul
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
          aria-label="Developer resources"
        >
          {LINKS.map((link) => (
            <li key={link.href}>
              <Link
                href={link.href}
                target={link.href.startsWith("http") ? "_blank" : undefined}
                rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                className="border-border bg-card hover:border-foreground/20 hover:bg-foreground/[0.02] group flex items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors"
              >
                <span className="text-foreground text-sm font-medium">{link.label}</span>
                <ArrowUpRight
                  size={14}
                  className="text-muted-foreground group-hover:text-foreground shrink-0 transition-colors"
                  aria-hidden="true"
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
